// @tennis-matches/services/winterdoppel/seriesConflicts

import { normalizeWinterDoppelPlayerName } from "./playerDisplay.js";
import { waitlistReserves, playingStarters } from "./planning.js";
import {
  WINTERDOPPEL_PLAN_MATRIX_WARNING,
  buildWinterDoppelPlanMatrixWarnings,
} from "./planMatrix.js";

export const WINTERDOPPEL_SERIES_CONFLICT = {
  LOCATION_OVERLAP: "location_overlap",
  INSUFFICIENT_DOUBLES: WINTERDOPPEL_PLAN_MATRIX_WARNING.INSUFFICIENT_DOUBLES,
  INSUFFICIENT_PLAYERS: WINTERDOPPEL_PLAN_MATRIX_WARNING.INSUFFICIENT_PLAYERS,
  THREE_IN_DOUBLE: WINTERDOPPEL_PLAN_MATRIX_WARNING.THREE_IN_DOUBLE,
  COURT_MIX_SUGGESTION: WINTERDOPPEL_PLAN_MATRIX_WARNING.COURT_MIX_SUGGESTION,
};

function __asStr(v) {
  return String(v ?? "").trim();
}

function __locationKey(locationId, seriesId) {
  const loc = __asStr(locationId);
  return loc || `series:${__asStr(seriesId)}`;
}

function __isPlannedAssignment(sp) {
  if (!sp) return false;
  if (sp.role === "starter" && sp.rsvp === "no") return false;
  return sp.role === "starter" || sp.role === "reserve";
}

/**
 * @param {object} player
 * @returns {{ key: string, label: string }}
 */
export function resolveWinterDoppelPlayerIdentity(player) {
  const club = __asStr(player?.club_person_id);
  const label = __asStr(player?.display_name) || __asStr(player?.id);
  if (club) return { key: `club:${club}`, label };
  const norm = normalizeWinterDoppelPlayerName(player?.display_name);
  if (norm) return { key: `name:${norm}`, label };
  return { key: `id:${__asStr(player?.id)}`, label };
}

/**
 * @param {{
 *   seriesId: string,
 *   seriesName?: string,
 *   locationId?: string|null,
 *   locationLabel?: string,
 *   players?: object[],
 *   sessions?: object[],
 *   sessionPlayers?: object[],
 * }} ctx
 */
export function collectWinterDoppelSeriesAssignments(ctx) {
  const seriesId = __asStr(ctx?.seriesId);
  if (!seriesId) return [];

  const playerById = new Map(
    (ctx.players || []).filter((p) => p?.id).map((p) => [__asStr(p.id), p]),
  );
  const sessionById = new Map(
    (ctx.sessions || [])
      .filter((s) => s?.id && s.status !== "cancelled")
      .map((s) => [__asStr(s.id), s]),
  );

  const out = [];
  for (const sp of ctx.sessionPlayers || []) {
    if (!__isPlannedAssignment(sp)) continue;
    const session = sessionById.get(__asStr(sp.session_id));
    if (!session) continue;
    const date = __asStr(session.session_date).slice(0, 10);
    if (!date) continue;
    const player = playerById.get(__asStr(sp.player_id));
    if (!player) continue;
    const identity = resolveWinterDoppelPlayerIdentity(player);
    out.push({
      seriesId,
      seriesName: __asStr(ctx.seriesName) || seriesId,
      seriesMode: __asStr(ctx.seriesMode) || "free",
      startTime: String(ctx.startTime || session.start_time || "").slice(0, 5),
      endTime: String(ctx.endTime || session.end_time || "").slice(0, 5),
      sessionId: __asStr(session.id),
      isHoliday: Boolean(session.isHoliday || session.status === "holiday"),
      locationId: __asStr(ctx.locationId) || null,
      locationLabel:
        __asStr(ctx.locationLabel) ||
        __asStr(ctx.locationId) ||
        __asStr(ctx.seriesName) ||
        seriesId,
      locationKey: __locationKey(ctx.locationId, seriesId),
      date,
      playerId: __asStr(player.id),
      playerName: identity.label,
      identityKey: identity.key,
      role: sp.role === "reserve" ? "reserve" : "starter",
      rsvp: __asStr(sp.rsvp) || "pending",
      courtNo: Number(sp.court_no) > 0 ? Number(sp.court_no) : null,
      waitlistRank:
        sp.role === "reserve" ? Number(sp.waitlist_rank) || null : null,
    });
  }
  return out;
}

/**
 * Spieler am selben Tag in Serien mit unterschiedlichem Ort (inkl. Warteliste).
 * @param {object[]} seriesContexts
 * @param {string} [focusSeriesId]
 */
export function buildWinterDoppelLocationConflicts(
  seriesContexts = [],
  focusSeriesId = "",
) {
  const focus = __asStr(focusSeriesId);
  const assignments = seriesContexts.flatMap((ctx) =>
    collectWinterDoppelSeriesAssignments(ctx),
  );

  /** @type {Map<string, object[]>} */
  const byIdentityDate = new Map();
  for (const row of assignments) {
    const bucketKey = `${row.identityKey}:${row.date}`;
    const list = byIdentityDate.get(bucketKey) || [];
    list.push(row);
    byIdentityDate.set(bucketKey, list);
  }

  const conflicts = [];
  for (const rows of byIdentityDate.values()) {
    const locationKeys = [...new Set(rows.map((r) => r.locationKey))];
    if (locationKeys.length < 2) continue;

    const touchesFocus = !focus || rows.some((r) => r.seriesId === focus);
    if (!touchesFocus) continue;

    conflicts.push({
      kind: WINTERDOPPEL_SERIES_CONFLICT.LOCATION_OVERLAP,
      date: rows[0].date,
      playerName: rows[0].playerName,
      identityKey: rows[0].identityKey,
      entries: rows
        .slice()
        .sort(
          (a, b) =>
            a.seriesName.localeCompare(b.seriesName, "de") ||
            a.role.localeCompare(b.role, "de"),
        ),
    });
  }

  return conflicts.sort(
    (a, b) =>
      String(a.date).localeCompare(String(b.date)) ||
      String(a.playerName || "").localeCompare(
        String(b.playerName || ""),
        "de",
      ),
  );
}

/**
 * @param {{
 *   series?: object,
 *   sessions?: object[],
 *   sessionPlayers?: object[],
 * }} opts
 */
export function buildWinterDoppelCoverageConflicts({
  series = {},
  sessions = [],
  sessionPlayers = [],
} = {}) {
  if (__asStr(series.series_mode) === "training") return [];

  const courtCount = Math.max(1, Number(series.court_count) || 1);
  const warnings = buildWinterDoppelPlanMatrixWarnings({
    sessions,
    sessionPlayers,
    courtCount,
  });

  const sessionById = new Map(
    (sessions || []).filter((s) => s?.id).map((s) => [__asStr(s.id), s]),
  );

  return warnings.flatMap((row) =>
    row.warnings.map((warning) => ({
      kind: warning.kind,
      sessionId: row.sessionId,
      sessionDate: row.sessionDate,
      sessionStatus: sessionById.get(row.sessionId)?.status || "",
      courtNo: warning.courtNo,
      courts: warning.courts,
      needed: warning.needed,
      pool: warning.pool,
      doubles: warning.doubles,
      singles: warning.singles,
      waitlist: warning.waitlist,
      unusedCourts: warning.unusedCourts,
      starters: playingStarters(
        (sessionPlayers || []).filter(
          (sp) => __asStr(sp.session_id) === row.sessionId,
        ),
      ).length,
      reserves: waitlistReserves(
        (sessionPlayers || []).filter(
          (sp) => __asStr(sp.session_id) === row.sessionId,
        ),
      ).length,
    })),
  );
}

/**
 * @param {{
 *   focusSeries?: object,
 *   sessions?: object[],
 *   sessionPlayers?: object[],
 *   seriesContexts?: object[],
 * }} opts
 */
export function buildWinterDoppelSeriesConflictReport({
  focusSeries = {},
  sessions = [],
  sessionPlayers = [],
  seriesContexts = [],
} = {}) {
  const focusId = __asStr(focusSeries.id);
  const locationConflicts = buildWinterDoppelLocationConflicts(
    seriesContexts,
    focusId,
  );
  const coverageConflicts = buildWinterDoppelCoverageConflicts({
    series: focusSeries,
    sessions,
    sessionPlayers,
  });

  return {
    locationConflicts,
    coverageConflicts,
    hasConflicts: locationConflicts.length + coverageConflicts.length > 0,
  };
}
